'use client';

import { useMemo, useState } from 'react';
import { SmartLink } from '@/components/smart-link';
import { TESTIMONIAL_CATEGORIES, type TestimonialCategory } from '@/lib/testimonial-categories';

export interface ReviewItem {
  quote: string;
  /** Absent for the few quotes whose source line named a city instead of a program. */
  program?: string | undefined;
  /** Catalog path for the program, when its name matches one. */
  programHref?: string | undefined;
  company: string;
  name?: string | undefined;
  logo?: string | undefined;
  category: TestimonialCategory;
}

export interface ReviewWallProps {
  items: readonly ReviewItem[];
  /** Cards per "View more" step. */
  pageSize?: number;
}

const ALL = 'all';
const DEFAULT_PAGE_SIZE = 12;
/** Quotes longer than this start clamped, with a button to read the rest. */
const CLAMP_CHARS = 220;

/**
 * Every client comment as a wall: filter by program family, search by
 * company or program, twelve at a time behind "View more" with a running
 * count. The volume is the proof, so nothing is hidden in a carousel.
 */
export function ReviewWall({ items, pageSize = DEFAULT_PAGE_SIZE }: ReviewWallProps) {
  const [category, setCategory] = useState<string>(ALL);
  const [query, setQuery] = useState('');
  const [shown, setShown] = useState(pageSize);
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set());

  const counts = useMemo(() => {
    const byCategory: Record<string, number> = {};
    for (const item of items) byCategory[item.category] = (byCategory[item.category] ?? 0) + 1;
    return byCategory;
  }, [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter(
      (item) =>
        (category === ALL || item.category === category) &&
        (q === '' || item.company.toLowerCase().includes(q) || (item.program?.toLowerCase().includes(q) ?? false)),
    );
  }, [items, category, query]);

  const visible = filtered.slice(0, shown);

  const select = (next: string) => {
    setCategory(next);
    setShown(pageSize);
  };

  return (
    <div>
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filter reviews by activity type">
          {[{ slug: ALL, label: 'All' }, ...TESTIMONIAL_CATEGORIES].map((option) => {
            const active = category === option.slug;
            const count = option.slug === ALL ? items.length : (counts[option.slug] ?? 0);
            return (
              <button
                key={option.slug}
                type="button"
                aria-pressed={active}
                onClick={() => select(option.slug)}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                  active ? 'bg-brand-royal text-white' : 'bg-white text-brand-navy shadow-sm hover:bg-brand-royal/10'
                }`}
              >
                {option.label} <span className={active ? 'text-white/85' : 'text-brand-navy/70'}>{count}</span>
              </button>
            );
          })}
        </div>
        <label className="relative block md:w-72">
          <span className="sr-only">Search reviews by company or activity</span>
          <input
            type="search"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setShown(pageSize);
            }}
            placeholder="Search by company or activity"
            className="w-full rounded-full border border-brand-navy/10 bg-white px-4 py-2.5 pl-10 text-sm text-brand-navy shadow-sm placeholder:text-brand-navy/70 focus:border-brand-royal focus:outline-none focus:ring-2 focus:ring-brand-royal/25"
          />
          <svg aria-hidden viewBox="0 0 24 24" className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-brand-navy/70" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" strokeLinecap="round" />
          </svg>
        </label>
      </div>

      <p className="mt-4 text-sm text-brand-navy/70" aria-live="polite">
        Showing {Math.min(visible.length, filtered.length)} of {filtered.length} reviews
      </p>

      {filtered.length === 0 ? (
        <p className="mt-8 rounded-2xl bg-white p-8 text-center text-brand-navy/70">No reviews match that search yet.</p>
      ) : (
        <ul className="mt-6 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {visible.map((item, index) => {
            const long = item.quote.length > CLAMP_CHARS;
            const open = expanded.has(index);
            return (
              <li key={`${item.company}-${index}`} className="flex flex-col rounded-2xl bg-white p-6 shadow-sm">
                {item.program ? (
                  <p className="mb-3 text-xs font-semibold tracking-wide text-brand-royal">
                    {item.programHref ? (
                      <SmartLink href={item.programHref} className="hover:underline">
                        {item.program}
                      </SmartLink>
                    ) : (
                      item.program
                    )}
                  </p>
                ) : null}
                <blockquote className={`flex-1 text-[15px] leading-relaxed text-brand-navy ${long && !open ? 'line-clamp-5' : ''}`}>
                  “{item.quote}”
                </blockquote>
                {long ? (
                  <button
                    type="button"
                    onClick={() =>
                      setExpanded((prev) => {
                        const next = new Set(prev);
                        if (next.has(index)) next.delete(index);
                        else next.add(index);
                        return next;
                      })
                    }
                    className="mt-2 self-start text-sm font-bold text-brand-royal hover:underline"
                  >
                    {open ? 'Show less' : 'Read the full review'}
                  </button>
                ) : null}
                <div className="mt-4 flex items-center gap-3 border-t border-brand-navy/10 pt-4">
                  {item.logo ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={item.logo}
                      alt={item.company}
                      loading="lazy"
                      className="h-9 w-14 shrink-0 rounded-md border border-brand-navy/5 bg-white object-contain p-1"
                    />
                  ) : (
                    <span aria-hidden className="flex size-9 shrink-0 items-center justify-center rounded-md bg-brand-royal/10 text-sm font-bold text-brand-royal">
                      {item.company.charAt(0)}
                    </span>
                  )}
                  <p className="min-w-0 text-sm">
                    {item.name ? <span className="block font-bold text-brand-navy">{item.name}</span> : null}
                    <span className="block truncate font-semibold text-brand-navy/70">{item.company}</span>
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {visible.length < filtered.length ? (
        <div className="mt-8 text-center">
          <button
            type="button"
            onClick={() => setShown((n) => n + pageSize)}
            className="rounded-full border-2 border-brand-royal px-6 py-2.5 text-sm font-bold text-brand-royal transition-colors hover:bg-brand-royal hover:text-white"
          >
            View more reviews
          </button>
        </div>
      ) : null}
    </div>
  );
}
